'use client';

/**
 * Data Classification Tool
 * Classify data sensitivity for compliance
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import ToolSEO from '@/components/ToolSEO';
import * as XLSX from 'xlsx';
import {
  DataClassificationInventory,
  DataAsset,
  DataClassificationLevel,
  DataCategory,
  RegulationApplicable,
  CLASSIFICATION_CONFIG,
  CATEGORY_CONFIG,
  REGULATION_CONFIG,
  generateAssetId,
  generateInventoryId,
} from '@/lib/data-classification/types';
import {
  saveInventoryToStorage,
  loadInventoryFromStorage,
  listInventoriesFromStorage,
  deleteInventoryFromStorage,
} from '@/lib/data-classification/storage';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };
type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    maxInventories: tier === 'free' ? 1 : tier === 'pro' ? 5 : Infinity,
    maxAssets: tier === 'free' ? 20 : tier === 'pro' ? 100 : Infinity,
    canExport: tierLevel >= TIER_HIERARCHY.free,
  };
}

function createDefaultInventory(): DataClassificationInventory {
  const now = new Date().toISOString();
  return {
    id: generateInventoryId(),
    name: 'Data Inventory',
    description: '',
    assets: [],
    settings: {},
    created_at: now,
    updated_at: now,
  };
}

export default function DataClassificationPage() {
  // State
  const [userTier] = useState<UserTier>('pro');
  const [inventories, setInventories] = useState<DataClassificationInventory[]>([]);
  const [currentInventory, setCurrentInventory] = useState<DataClassificationInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor states
  const [editingAsset, setEditingAsset] = useState<DataAsset | null>(null);
  const [isAddingAsset, setIsAddingAsset] = useState(false);
  const [filterClassification, setFilterClassification] = useState<DataClassificationLevel | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<DataCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const features = getFeatureAccess(userTier);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load inventories
  const loadInventories = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listInventoriesFromStorage();
      setInventories(list);
    } catch (err) {
      console.error('[DataClassification] Failed to load:', err);
      setError('Failed to load inventories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventories();
  }, [loadInventories]);

  // Auto-save
  useEffect(() => {
    if (!currentInventory || !hasChanges) return;

    const timer = setTimeout(async () => {
      try {
        setSaving(true);
        const updated = { ...currentInventory, updated_at: new Date().toISOString() };
        await saveInventoryToStorage(updated);
        setCurrentInventory(updated);
        setHasChanges(false);
        setInventories((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      } catch (err) {
        console.error('[DataClassification] Save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentInventory, hasChanges]);

  // Handlers
  const handleCreateInventory = async () => {
    if (inventories.length >= features.maxInventories) {
      setError(`Free tier limited to ${features.maxInventories} inventory. Upgrade to Pro for more.`);
      return;
    }

    const newInventory = createDefaultInventory();
    await saveInventoryToStorage(newInventory);
    setInventories((prev) => [newInventory, ...prev]);
    setCurrentInventory(newInventory);
  };

  const handleOpenInventory = async (id: string) => {
    try {
      const inventory = await loadInventoryFromStorage(id);
      if (inventory) {
        setCurrentInventory(inventory);
        setHasChanges(false);
      }
    } catch (err) {
      console.error('[DataClassification] Failed to open:', err);
      setError('Failed to open inventory');
    }
  };

  const handleDeleteInventory = async (id: string) => {
    if (!confirm('Delete this data inventory?')) return;
    await deleteInventoryFromStorage(id);
    setInventories((prev) => prev.filter((i) => i.id !== id));
    if (currentInventory?.id === id) {
      setCurrentInventory(null);
    }
  };

  const handleSaveAsset = (asset: DataAsset) => {
    if (!currentInventory) return;

    if (currentInventory.assets.length >= features.maxAssets && !currentInventory.assets.find((a) => a.id === asset.id)) {
      setError(`Limited to ${features.maxAssets} assets. Upgrade for more.`);
      return;
    }

    const existingIdx = currentInventory.assets.findIndex((a) => a.id === asset.id);
    let newAssets: DataAsset[];
    if (existingIdx >= 0) {
      newAssets = [...currentInventory.assets];
      newAssets[existingIdx] = asset;
    } else {
      newAssets = [...currentInventory.assets, asset];
    }

    setCurrentInventory({ ...currentInventory, assets: newAssets });
    setHasChanges(true);
    setEditingAsset(null);
    setIsAddingAsset(false);
  };

  const handleDeleteAsset = (id: string) => {
    if (!currentInventory) return;
    setCurrentInventory({
      ...currentInventory,
      assets: currentInventory.assets.filter((a) => a.id !== id),
    });
    setHasChanges(true);
    setEditingAsset(null);
  };

  const handleExportCSV = () => {
    if (!currentInventory) return;

    const headers = [
      'Name',
      'Description',
      'Category',
      'Classification',
      'Location',
      'Owner',
      'Regulations',
      'Encryption',
      'Backup',
      'Retention',
      'Last Review',
    ];

    const rows = currentInventory.assets.map((asset) => [
      asset.name,
      asset.description || '',
      CATEGORY_CONFIG[asset.category].label,
      CLASSIFICATION_CONFIG[asset.classification].label,
      asset.location,
      asset.owner || '',
      asset.regulations.map((r) => REGULATION_CONFIG[r].label).join('; '),
      asset.encryptionStatus,
      asset.backupStatus,
      asset.retentionPeriod || '',
      asset.lastReviewDate || '',
    ]);

    const csv = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentInventory.name.replace(/\s+/g, '-').toLowerCase()}-data-classification.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    // Create workbook with template
    const wb = XLSX.utils.book_new();

    // Template data with headers and example rows
    const templateData = [
      ['Name', 'Description', 'Category', 'Classification', 'Location', 'Owner', 'Regulations', 'Encryption', 'Backup', 'Retention Period', 'Last Review Date', 'Notes'],
      ['Customer Database', 'Main customer records including PII', 'personal_data', 'confidential', 'AWS RDS (us-east-1)', 'Data Team', 'gdpr;ccpa', 'encrypted', 'backed_up', '7 years', '2024-01-15', 'Contains SSN and payment info'],
      ['Public Blog Posts', 'Published marketing content', 'business_data', 'public', 'WordPress', 'Marketing', '', 'not_encrypted', 'backed_up', 'indefinite', '', ''],
      ['Employee Records', 'HR employee database', 'personal_data', 'restricted', 'SAP HR Module', 'HR Department', 'gdpr;hipaa', 'encrypted', 'backed_up', '10 years', '2024-06-01', 'Includes health information'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Name
      { wch: 40 }, // Description
      { wch: 15 }, // Category
      { wch: 15 }, // Classification
      { wch: 25 }, // Location
      { wch: 15 }, // Owner
      { wch: 20 }, // Regulations
      { wch: 15 }, // Encryption
      { wch: 12 }, // Backup
      { wch: 15 }, // Retention
      { wch: 15 }, // Last Review
      { wch: 30 }, // Notes
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Data Assets');

    // Add reference sheet for valid values
    const refData = [
      ['Field', 'Valid Values', 'Notes'],
      ['Category', 'personal_data, financial, health_data, intellectual_property, business_data, credentials, logs, metadata, other', 'Use exact value'],
      ['Classification', 'public, internal, confidential, restricted', 'Use exact value'],
      ['Encryption', 'encrypted, not_encrypted, partial, unknown', 'Use exact value'],
      ['Backup', 'backed_up, not_backed_up, unknown', 'Use exact value'],
      ['Regulations', 'gdpr;ccpa;hipaa;pci_dss;sox;ferpa;glba;lgpd', 'Separate multiple with semicolon'],
    ];

    const refWs = XLSX.utils.aoa_to_sheet(refData);
    refWs['!cols'] = [{ wch: 15 }, { wch: 70 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, refWs, 'Reference Values');

    // Download
    XLSX.writeFile(wb, 'data-classification-template.xlsx');
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentInventory) return;

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });

      if (rows.length === 0) {
        setError('No data found in the Excel file');
        return;
      }

      const newAssets: DataAsset[] = [];
      const errors: string[] = [];

      rows.forEach((row, index) => {
        const rowNum = index + 2; // Account for header row

        // Map Excel columns to asset properties (flexible column naming)
        const name = row['Name'] || row['name'] || row['Asset Name'] || '';
        const description = row['Description'] || row['description'] || '';
        const categoryRaw = row['Category'] || row['category'] || '';
        const classificationRaw = row['Classification'] || row['classification'] || '';
        const location = row['Location'] || row['location'] || '';
        const owner = row['Owner'] || row['owner'] || '';
        const regulationsRaw = row['Regulations'] || row['regulations'] || '';
        const encryptionRaw = row['Encryption'] || row['encryption'] || 'unknown';
        const backupRaw = row['Backup'] || row['backup'] || 'unknown';
        const retentionPeriod = row['Retention Period'] || row['retention'] || '';
        const lastReviewDate = row['Last Review Date'] || row['last_review'] || '';
        const notes = row['Notes'] || row['notes'] || '';

        // Validate required fields
        if (!name.trim()) {
          errors.push(`Row ${rowNum}: Name is required`);
          return;
        }
        if (!location.trim()) {
          errors.push(`Row ${rowNum}: Location is required`);
          return;
        }

        // Parse category
        const category = (categoryRaw.toLowerCase().replace(/\s+/g, '_') || 'business_data') as DataCategory;
        if (!CATEGORY_CONFIG[category]) {
          errors.push(`Row ${rowNum}: Invalid category "${categoryRaw}"`);
          return;
        }

        // Parse classification
        const classification = (classificationRaw.toLowerCase() || 'internal') as DataClassificationLevel;
        if (!CLASSIFICATION_CONFIG[classification]) {
          errors.push(`Row ${rowNum}: Invalid classification "${classificationRaw}"`);
          return;
        }

        // Parse regulations (semicolon-separated)
        const regulations: RegulationApplicable[] = [];
        if (regulationsRaw) {
          regulationsRaw.split(/[;,]/).forEach(r => {
            const reg = r.trim().toLowerCase().replace(/\s+/g, '_') as RegulationApplicable;
            if (REGULATION_CONFIG[reg]) {
              regulations.push(reg);
            }
          });
        }

        // Parse encryption status
        const encryptionMap: Record<string, DataAsset['encryptionStatus']> = {
          'encrypted': 'encrypted',
          'not_encrypted': 'not_encrypted',
          'not encrypted': 'not_encrypted',
          'partial': 'partial',
          'partially': 'partial',
          'unknown': 'unknown',
        };
        const encryptionStatus = encryptionMap[encryptionRaw.toLowerCase()] || 'unknown';

        // Parse backup status
        const backupMap: Record<string, DataAsset['backupStatus']> = {
          'backed_up': 'backed_up',
          'backed up': 'backed_up',
          'yes': 'backed_up',
          'not_backed_up': 'not_backed_up',
          'not backed up': 'not_backed_up',
          'no': 'not_backed_up',
          'unknown': 'unknown',
        };
        const backupStatus = backupMap[backupRaw.toLowerCase()] || 'unknown';

        newAssets.push({
          id: generateAssetId(),
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          classification,
          dataTypes: [],
          location: location.trim(),
          owner: owner.trim() || undefined,
          regulations,
          encryptionStatus,
          backupStatus,
          retentionPeriod: retentionPeriod.trim() || undefined,
          lastReviewDate: lastReviewDate.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      });

      if (errors.length > 0) {
        setError(`Import errors:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''}`);
      }

      if (newAssets.length > 0) {
        // Check asset limit
        const totalAssets = currentInventory.assets.length + newAssets.length;
        if (totalAssets > features.maxAssets) {
          setError(`Cannot import ${newAssets.length} assets. Would exceed limit of ${features.maxAssets}.`);
          return;
        }

        setCurrentInventory({
          ...currentInventory,
          assets: [...currentInventory.assets, ...newAssets],
        });
        setHasChanges(true);
        setError(null);
      }
    } catch (err) {
      console.error('[DataClassification] Import failed:', err);
      setError('Failed to import Excel file. Please check the format.');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Filtered assets
  const filteredAssets =
    currentInventory?.assets.filter((asset) => {
      if (filterClassification !== 'all' && asset.classification !== filterClassification) return false;
      if (filterCategory !== 'all' && asset.category !== filterCategory) return false;
      if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    }) || [];

  // Classification summary
  const classificationSummary = currentInventory
    ? {
        public: currentInventory.assets.filter((a) => a.classification === 'public').length,
        internal: currentInventory.assets.filter((a) => a.classification === 'internal').length,
        confidential: currentInventory.assets.filter((a) => a.classification === 'confidential').length,
        restricted: currentInventory.assets.filter((a) => a.classification === 'restricted').length,
      }
    : { public: 0, internal: 0, confidential: 0, restricted: 0 };

  // Inventory list view
  if (!currentInventory) {
    return (
      <>
        <ToolSEO
          toolName="Data Classification Tool"
          toolSlug="data-classification"
          description="Classify data assets by sensitivity level for compliance with GDPR, HIPAA, PCI DSS and other regulations."
          category="assessment"
          isPremium={true}
        />
        <main className="min-h-screen bg-background text-foreground py-12 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold">Data Classification Tool</h1>
                <p className="text-foreground-secondary mt-2">Classify data assets by sensitivity for compliance</p>
              </div>
              <button
                onClick={handleCreateInventory}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + New Inventory
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {error}
                <button onClick={() => setError(null)} className="ml-4 underline">
                  Dismiss
                </button>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading inventories...</p>
              </div>
            ) : inventories.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-12 text-center">
                <div className="text-4xl mb-4">🗂️</div>
                <h3 className="text-lg font-medium mb-2">No data inventories yet</h3>
                <p className="text-foreground-tertiary mb-6">Create your first data classification inventory</p>
                <button
                  onClick={handleCreateInventory}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
                >
                  Create Inventory
                </button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {inventories.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-6 border border-border rounded-lg hover:border-accent/40 transition-colors cursor-pointer group"
                    onClick={() => handleOpenInventory(inv.id)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                          {inv.name}
                        </h3>
                        {inv.description && (
                          <p className="text-sm text-foreground-tertiary mt-1 line-clamp-2">{inv.description}</p>
                        )}
                      </div>
                      <span className="text-sm text-foreground-muted">{inv.assets.length} assets</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-muted">
                      <span>Updated {new Date(inv.updated_at).toLocaleDateString()}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteInventory(inv.id);
                        }}
                        className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Classification guide */}
            <div className="mt-12 p-6 bg-cream/5 border border-border-muted rounded-lg">
              <h3 className="font-semibold mb-4">Classification Levels</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {(['public', 'internal', 'confidential', 'restricted'] as DataClassificationLevel[]).map((level) => {
                  const config = CLASSIFICATION_CONFIG[level];
                  return (
                    <div key={level} className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full mt-1" style={{ backgroundColor: config.color }} />
                      <div>
                        <div className="font-medium text-foreground">{config.label}</div>
                        <div className="text-sm text-foreground-tertiary">{config.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Inventory editor view
  return (
    <>
      <ToolSEO
        toolName="Data Classification Tool"
        toolSlug="data-classification"
        description="Classify data assets by sensitivity level for compliance with GDPR, HIPAA, PCI DSS and other regulations."
        category="assessment"
        isPremium={true}
      />
      <main className="min-h-screen bg-background text-foreground py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentInventory(null)}
                className="text-foreground-tertiary hover:text-foreground transition-colors"
              >
                ← Back
              </button>
              <div>
                <input
                  type="text"
                  value={currentInventory.name}
                  onChange={(e) => {
                    setCurrentInventory({ ...currentInventory, name: e.target.value });
                    setHasChanges(true);
                  }}
                  className="text-2xl font-bold bg-transparent border-none text-foreground focus:outline-none"
                />
                <div className="text-sm text-foreground-tertiary">
                  {currentInventory.assets.length} assets
                  {saving && <span className="ml-2 text-accent">Saving...</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                title="Download Excel template"
              >
                📥 Template
              </button>
              <label className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors cursor-pointer">
                📤 Import Excel
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  className="hidden"
                />
              </label>
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
              >
                Export CSV
              </button>
              <button
                onClick={() => setIsAddingAsset(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + Add Asset
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">
                Dismiss
              </button>
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {(['public', 'internal', 'confidential', 'restricted'] as DataClassificationLevel[]).map((level) => {
              const config = CLASSIFICATION_CONFIG[level];
              const count = classificationSummary[level];
              return (
                <div
                  key={level}
                  className="p-4 border border-border-muted rounded-lg cursor-pointer hover:border-border transition-colors"
                  onClick={() => setFilterClassification(filterClassification === level ? 'all' : level)}
                  style={{
                    borderColor: filterClassification === level ? config.color : undefined,
                    backgroundColor: filterClassification === level ? `${config.color}10` : undefined,
                  }}
                >
                  <div className="text-2xl font-bold" style={{ color: config.color }}>
                    {count}
                  </div>
                  <div className="text-sm text-foreground-tertiary">{config.label}</div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assets..."
              className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground placeholder-foreground-muted"
            />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as DataCategory | 'all')}
              className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
            >
              <option value="all">All Categories</option>
              {(Object.keys(CATEGORY_CONFIG) as DataCategory[]).map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_CONFIG[cat].label}
                </option>
              ))}
            </select>
          </div>

          {/* Asset list */}
          {filteredAssets.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <div className="text-4xl mb-4">📄</div>
              <h3 className="text-lg font-medium mb-2">
                {currentInventory.assets.length === 0 ? 'No assets yet' : 'No assets match filters'}
              </h3>
              <p className="text-foreground-tertiary mb-6">Add data assets to classify them</p>
              <button
                onClick={() => setIsAddingAsset(true)}
                className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                Add Asset
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAssets.map((asset) => {
                const classConfig = CLASSIFICATION_CONFIG[asset.classification];
                const catConfig = CATEGORY_CONFIG[asset.category];
                return (
                  <div
                    key={asset.id}
                    className="p-4 border border-border-muted rounded-lg hover:border-border cursor-pointer transition-colors"
                    onClick={() => setEditingAsset(asset)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-xl">{catConfig.icon}</span>
                        <div>
                          <div className="font-medium text-foreground">{asset.name}</div>
                          <div className="text-sm text-foreground-tertiary mt-1">
                            {asset.location}
                            {asset.owner && ` · Owner: ${asset.owner}`}
                          </div>
                          {asset.regulations.length > 0 && asset.regulations[0] !== 'none' && (
                            <div className="flex gap-1 mt-2">
                              {asset.regulations.map((reg) => (
                                <span
                                  key={reg}
                                  className="px-2 py-0.5 bg-cream/10 text-foreground-secondary rounded text-xs"
                                >
                                  {REGULATION_CONFIG[reg].label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex gap-2">
                          {asset.encryptionStatus === 'encrypted' && (
                            <span className="text-green-400 text-sm" title="Encrypted">
                              🔒
                            </span>
                          )}
                          {asset.encryptionStatus === 'not_encrypted' && (
                            <span className="text-red-400 text-sm" title="Not Encrypted">
                              🔓
                            </span>
                          )}
                        </div>
                        <span
                          className="px-3 py-1 rounded text-sm font-medium"
                          style={{
                            backgroundColor: `${classConfig.color}20`,
                            color: classConfig.color,
                          }}
                        >
                          {classConfig.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Asset Editor Modal */}
          {(editingAsset || isAddingAsset) && (
            <AssetEditorModal
              asset={editingAsset}
              isNew={isAddingAsset}
              onClose={() => {
                setEditingAsset(null);
                setIsAddingAsset(false);
              }}
              onSave={handleSaveAsset}
              onDelete={handleDeleteAsset}
            />
          )}
        </div>
      </main>
    </>
  );
}

// Asset Editor Modal Component
function AssetEditorModal({
  asset,
  isNew,
  onClose,
  onSave,
  onDelete,
}: {
  asset: DataAsset | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (asset: DataAsset) => void;
  onDelete: (id: string) => void;
}) {
  const [formData, setFormData] = useState<DataAsset>(() => {
    if (asset) return asset;
    return {
      id: generateAssetId(),
      name: '',
      description: '',
      category: 'business_data',
      classification: 'internal',
      dataTypes: [],
      location: '',
      owner: '',
      regulations: [],
      encryptionStatus: 'unknown',
      backupStatus: 'unknown',
    };
  });

  const [dataTypeInput, setDataTypeInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border-muted">
          <h3 className="text-lg font-semibold text-foreground">{isNew ? 'Add Data Asset' : 'Edit Data Asset'}</h3>
          <button onClick={onClose} className="p-1 text-foreground-tertiary hover:text-foreground">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Asset Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Customer Database"
              className={`w-full px-3 py-2 bg-background/60 border rounded-lg text-foreground ${
                errors.name ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this data asset..."
              rows={2}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
            />
          </div>

          {/* Category & Classification */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as DataCategory })}
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              >
                {(Object.keys(CATEGORY_CONFIG) as DataCategory[]).map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_CONFIG[cat].icon} {CATEGORY_CONFIG[cat].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Classification *</label>
              <select
                value={formData.classification}
                onChange={(e) =>
                  setFormData({ ...formData, classification: e.target.value as DataClassificationLevel })
                }
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              >
                {(Object.keys(CLASSIFICATION_CONFIG) as DataClassificationLevel[]).map((level) => (
                  <option key={level} value={level}>
                    {CLASSIFICATION_CONFIG[level].label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Location & Owner */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., AWS S3, PostgreSQL"
                className={`w-full px-3 py-2 bg-background/60 border rounded-lg text-foreground ${
                  errors.location ? 'border-red-500' : 'border-border'
                }`}
              />
              {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Owner</label>
              <input
                type="text"
                value={formData.owner || ''}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="e.g., Data Team"
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              />
            </div>
          </div>

          {/* Regulations */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">Applicable Regulations</label>
            <div className="grid grid-cols-4 gap-2">
              {(Object.keys(REGULATION_CONFIG) as RegulationApplicable[])
                .filter((r) => r !== 'none')
                .map((reg) => (
                  <label key={reg} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.regulations.includes(reg)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, regulations: [...formData.regulations, reg] });
                        } else {
                          setFormData({
                            ...formData,
                            regulations: formData.regulations.filter((r) => r !== reg),
                          });
                        }
                      }}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-foreground-secondary">{REGULATION_CONFIG[reg].label}</span>
                  </label>
                ))}
            </div>
          </div>

          {/* Encryption & Backup */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Encryption Status</label>
              <select
                value={formData.encryptionStatus}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    encryptionStatus: e.target.value as DataAsset['encryptionStatus'],
                  })
                }
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              >
                <option value="encrypted">Encrypted</option>
                <option value="not_encrypted">Not Encrypted</option>
                <option value="partial">Partially Encrypted</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Backup Status</label>
              <select
                value={formData.backupStatus}
                onChange={(e) =>
                  setFormData({ ...formData, backupStatus: e.target.value as DataAsset['backupStatus'] })
                }
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              >
                <option value="backed_up">Backed Up</option>
                <option value="not_backed_up">Not Backed Up</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>

          {/* Retention & Review */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Retention Period</label>
              <input
                type="text"
                value={formData.retentionPeriod || ''}
                onChange={(e) => setFormData({ ...formData, retentionPeriod: e.target.value })}
                placeholder="e.g., 7 years"
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Last Review Date</label>
              <input
                type="date"
                value={formData.lastReviewDate || ''}
                onChange={(e) => setFormData({ ...formData, lastReviewDate: e.target.value })}
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Notes</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={2}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border-muted">
            <div>
              {!isNew && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Delete this asset?')) onDelete(formData.id);
                  }}
                  className="px-3 py-2 text-sm text-red-400 hover:text-red-300"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground-tertiary hover:text-foreground">
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover"
              >
                {isNew ? 'Add Asset' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
